import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Box,
  Chip,
  IconButton
} from '@mui/material';
import {
  Cake as CakeIcon,
  Favorite as HeartIcon,
  ArrowForward as ArrowIcon,
  Celebration as CelebrationIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

const BirthdaysWidget = ({ birthdays, loading }) => {
  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<CakeIcon color="secondary" />}
          title="Birthdays & Anniversaries"
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!birthdays || birthdays.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<CakeIcon color="secondary" />}
          title="Birthdays & Anniversaries"
          action={
            <IconButton component={Link} to="/birthdays">
              <ArrowIcon />
            </IconButton>
          }
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No upcoming celebrations in the next 30 days.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getDaysLabel = (daysUntil) => {
    if (daysUntil === 0) return 'Today!';
    if (daysUntil === 1) return 'Tomorrow';
    return `In ${daysUntil} days`;
  };

  const getChipColor = (daysUntil) => {
    if (daysUntil === 0) return 'error';
    if (daysUntil <= 7) return 'warning';
    return 'default';
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<CakeIcon color="secondary" />}
        title="Birthdays & Anniversaries"
        action={
          <IconButton component={Link} to="/birthdays" title="View birthday calendar">
            <ArrowIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <List dense>
          {birthdays.map((item) => (
            <ListItem
              key={item.id}
              sx={{
                borderRadius: 1,
                mb: 1,
                bgcolor: item.isToday ? 'action.selected' : 'background.default',
                border: item.isToday ? 2 : 0,
                borderColor: item.isToday ? 'secondary.main' : 'transparent',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={item.photoUrl}
                  sx={{
                    bgcolor: item.type === 'birthday' ? 'secondary.main' : 'error.main'
                  }}
                >
                  {item.type === 'birthday' ? <CakeIcon /> : <HeartIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                    {item.isToday && (
                      <CelebrationIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.type === 'birthday'
                        ? `Turning ${item.age} on ${format(item.date, 'MMMM d')}`
                        : `${item.years} year anniversary on ${format(item.date, 'MMMM d')}`
                      }
                    </Typography>
                    <Chip
                      label={getDaysLabel(item.daysUntil)}
                      size="small"
                      color={getChipColor(item.daysUntil)}
                      sx={{ height: 22 }}
                    />
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default BirthdaysWidget;
